import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { fetchMasterData, resetState as resetMasterData } from './masterDataSlice';
import { API_BASE_URL } from '../../utils/apiConfig';

// Login user
export const loginUser = createAsyncThunk(
  'auth/login',
  async ({ loginID, password, role }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/login`, {
        loginID,
        password,
        role
      });

      const { token, user } = response.data;
      // Store token and user in localStorage
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      
      // Fetch master data immediately after successful login
      await dispatch(fetchMasterData()).unwrap();
      
      return response.data;
    } catch (error) {
      // Check if it's an invalid credentials error
      if (error.response?.status === 401 && error.response?.data?.message === 'Invalid credentials') {
        return rejectWithValue('Wrong credentials');
      }
      return rejectWithValue(
        error.response?.data?.message || 'Login failed. Please try again.'
      );
    }
  }
);

// Signup user
export const signupUser = createAsyncThunk(
  'auth/signup',
  async ({ loginID, password, role, firstName, lastName, email }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/signup`, {
        loginID,
        password,
        role,
        firstName,
        lastName,
        email
      });

      const { token, user } = response.data;
      
      // Store token and user in localStorage
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      
      // Fetch master data immediately after successful signup
      await dispatch(fetchMasterData()).unwrap();
      
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Signup failed. Please try again.'
      );
    }
  }
);

// Verify token
export const verifyToken = createAsyncThunk(
  'auth/verify',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No token found');
      }

      const response = await axios.post(
        `${API_BASE_URL}/auth/verify`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      // Update localStorage with fresh user data
      localStorage.setItem('user', JSON.stringify(response.data.user));

      // Fetch master data immediately after successful token verification
      await dispatch(fetchMasterData()).unwrap();

      return response.data;
    } catch (error) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      return rejectWithValue('Session expired. Please login again.');
    }
  }
);

// Initialize app (for page refresh scenarios)
export const initializeApp = createAsyncThunk(
  'auth/initialize',
  async (_, { dispatch, getState, rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');

      // If no token, return null (user needs to login)
      if (!token) {
        return null;
      }

      // Always verify token on app load to get fresh user data (including permissions)
      // so that permission changes (e.g. position updates) take effect after refresh
      try {
        console.log('Calling /auth/verify to get fresh user data...');
        const response = await axios.post(
          `${API_BASE_URL}/auth/verify`,
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );

        console.log('Verify response:', response.data);

        // Use fresh user data from backend
        const authData = {
          success: true,
          user: response.data.user,
          token: token
        };

        // Update localStorage with fresh user data
        localStorage.setItem('user', JSON.stringify(response.data.user));

        console.log('Updated localStorage with user:', response.data.user);

        // Fetch master data immediately
        await dispatch(fetchMasterData()).unwrap();

        return authData;
      } catch (verifyError) {
        console.error('Token verification failed:', verifyError);
        // If token verification fails, clear localStorage
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        return rejectWithValue('Session expired. Please login again.');
      }
    } catch (error) {
      console.error('Initialize app error:', error);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      return rejectWithValue('Session expired. Please login again.');
    }
  }
);

const initialState = {
  user: (() => {
    try {
      const user = localStorage.getItem('user');
      if (user) {
        const parsedUser = JSON.parse(user);
        // Ensure user object has staff_id field (for backward compatibility)
        if (typeof parsedUser.staff_id === 'undefined') {
          parsedUser.staff_id = null;
        }
        return parsedUser;
      }
      return null;
    } catch (error) {
      console.error('Error parsing user from localStorage:', error);
      localStorage.removeItem('user');
      return null;
    }
  })(),
  token: localStorage.getItem('token'),
  isLoading: false,
  isAuthenticated: (() => {
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('user');
    return !!(token && user);
  })(),
  error: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state, action) => {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.error = null;
      
      // Log auth state
      console.log('Auth State:', {
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
        isLoading: state.isLoading,
        error: state.error,
        staff_id: state.user?.staff_id
      });
      
      // Clear master data and dashboard when logging out
      // Note: We'll dispatch resetMasterData in the component that calls logout
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login cases
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.error = null;
        
        // Log auth state
        console.log('Auth State:', {
          user: state.user,
          token: state.token,
          isAuthenticated: state.isAuthenticated,
          isLoading: state.isLoading,
          error: state.error,
          staff_id: state.user?.staff_id
        });
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.error = action.payload;
      })
      // Signup cases
      .addCase(signupUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(signupUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.error = null;
        
        // Log auth state
        console.log('Auth State:', {
          user: state.user,
          token: state.token,
          isAuthenticated: state.isAuthenticated,
          isLoading: state.isLoading,
          error: state.error,
          staff_id: state.user?.staff_id
        });
      })
      .addCase(signupUser.rejected, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.error = action.payload;
      })
      // Verify token cases
      .addCase(verifyToken.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(verifyToken.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        
        // Log auth state
        console.log('Auth State:', {
          user: state.user,
          token: state.token,
          isAuthenticated: state.isAuthenticated,
          isLoading: state.isLoading,
          error: state.error,
          staff_id: state.user?.staff_id
        });
      })
      .addCase(verifyToken.rejected, (state) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
      })
      // Initialize app cases
      .addCase(initializeApp.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(initializeApp.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload) {
          state.isAuthenticated = true;
          state.user = action.payload.user;
        }
        
        // Log auth state
        console.log('Auth State:', {
          user: state.user,
          token: state.token,
          isAuthenticated: state.isAuthenticated,
          isLoading: state.isLoading,
          error: state.error,
          staff_id: state.user?.staff_id
        });
      })
      .addCase(initializeApp.rejected, (state) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
      });
  },
});

export const { logout, clearError } = authSlice.actions;
export default authSlice.reducer; 